import React, { useState, useEffect, useMemo } from 'react';
import { Box, Button, MenuItem, Select, Typography, IconButton, Chip, Tooltip, useTheme } from '@mui/material';
import { Block, LockOpen, VolumeUp, RestartAlt, Settings, PinDrop, PhotoCamera } from '@mui/icons-material';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Scooter } from '../../resources/types/scootersTypes';
import { ScooterStatus } from '../../pages/ZonesPage/interface';
import {
  lockScooterAsync,
  signalScooterAsync,
  formatCDScooterAsync,
  updateScooterStatusAsync
} from '../../redux/slices/scootersSlice';
import ForceLocationModal from '../../modals/ForceLocationModal';
import TripPhotosModal from '../../modals/TripPhotosModal';

interface ScooterInfoWindowProps {
  scooter: Scooter;
  onOpenSettings?: () => void;
}

const hexToRgba = (hex: string, alpha: number): string => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const STATUSES_CONFIG = [
  { key: 'statusAvailable', value: ScooterStatus.AVAILABLE, color: '#07c50b' },
  { key: 'statusTransporting', value: ScooterStatus.TRANSPORTING, color: '#EA7F5C' },
  { key: 'statusCharging', value: ScooterStatus.CHARGING, color: '#742BFF' },
  { key: 'statusMaintenance', value: ScooterStatus.MAINTENANCE, color: '#9714E8' },
  { key: 'statusBrokenInCity', value: ScooterStatus.BROKEN_IN_CITY, color: '#2895EA' },
  { key: 'statusDischarged', value: ScooterStatus.LOW_BATTERY, color: '#151515' },
  { key: 'statusHidden', value: ScooterStatus.HIDDEN, color: '#757575' },
];

const ScooterInfoWindow: React.FC<ScooterInfoWindowProps> = ({ scooter, onOpenSettings }) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [status, setStatus] = useState(scooter.status || ScooterStatus.AVAILABLE);
  const [forceLocationOpen, setForceLocationOpen] = useState(false);
  const [tripPhotosOpen, setTripPhotosOpen] = useState(false);
  const hasForceLocation = Boolean(scooter.trackerCoords) || Boolean(scooter.hasForceCoords);

  const statusesOption = useMemo(
    () => STATUSES_CONFIG.map(s => ({ ...s, label: t(`control.${s.key}`) })),
    [t]
  );

  useEffect(() => {
    setStatus(scooter.status || ScooterStatus.AVAILABLE);
  }, [scooter.status]);

  const currentStatus = statusesOption.find(s => s.value === status) || statusesOption[0];
  
  const getTextColor = (statusValue: ScooterStatus, color: string) => {
    if (theme.palette.mode === 'dark' && 
        (statusValue === ScooterStatus.LOW_BATTERY || statusValue === ScooterStatus.HIDDEN)) {
      return '#FFFFFF';
    }
    return color;
  };

  const handleLockToggle = () => {
    dispatch(lockScooterAsync(scooter._id, { lock: !scooter.lock }));
  };

  const handleSignal = async () => {
    await dispatch(signalScooterAsync(scooter._id, { value: true }));
  };

  const handleFormat = () => {
    if (window.confirm(t('control.confirmFormat'))) {
      dispatch(formatCDScooterAsync(scooter._id));
    }
  };

  const handleStatusChange = (newStatus: ScooterStatus) => {
    setStatus(newStatus);
    dispatch(updateScooterStatusAsync(scooter._id, { status: newStatus }));
  };

  return (
    <Box sx={{ 
      minWidth: 220, 
      padding: 1
    }}>
      <Typography variant="body2" sx={{ mb: 1 }}>
        <strong>{t('control.scooter')}:</strong> {scooter.name}
      </Typography>
      {scooter?.battery && (
        <Typography variant="body2" sx={{ mb: 1 }}>
          <strong>{t('control.battery')}:</strong> {scooter.battery}%
        </Typography>
      )}
      
      <Box sx={{ mb: 1.5 }}>
        <Select
          value={status}
          onChange={(e) => handleStatusChange(e.target.value as ScooterStatus)}
          size="small"
          fullWidth
          MenuProps={{
            container: () => document.fullscreenElement || document.body,
            style: { zIndex: 9999999 },
            disablePortal: false
          }}
          sx={{ 
            fontSize: '12px',
            '& .MuiSelect-select': {
              py: 0.5,
              bgcolor: hexToRgba(currentStatus.color, 0.12),
              color: getTextColor(currentStatus.value, currentStatus.color),
              fontWeight: 500
            }
          }}
        >
          {statusesOption.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              <Chip
                label={option.label}
                size="small"
                sx={{
                  bgcolor: hexToRgba(option.color, 0.12),
                  color: getTextColor(option.value, option.color),
                  fontWeight: 500,
                  width: '100%'
                }}
              />
            </MenuItem>
          ))}
        </Select>
      </Box>

      <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-start' }}>
        <Tooltip title={scooter.lock ? t('control.locked') : t('control.unlocked')}>
          <IconButton
            onClick={handleLockToggle}
            size='medium'
            sx={{
              bgcolor: theme.palette.mode === 'light' ? '#F5F5F5' : '#424242',
              color: theme.palette.mode === 'light' ? '#616161' : '#FFF',
              borderRadius: '8px',
              '&:hover': { bgcolor: theme.palette.mode === 'light' ? '#E0E0E0' : '#3A3A3A' }
            }}
          >
            {scooter.lock ? <Block fontSize='small' /> : <LockOpen fontSize='small' />}
          </IconButton>
        </Tooltip>
        
        <Tooltip title={t('control.signal')}>
          <IconButton
            onClick={handleSignal}
            size='medium'
            sx={{
              bgcolor: theme.palette.mode === 'light' ? '#F5F5F5' : '#424242',
              color: theme.palette.mode === 'light' ? '#616161' : '#FFF',
              borderRadius: '8px',
              '&:hover': { bgcolor: theme.palette.mode === 'light' ? '#E0E0E0' : '#3A3A3A' }
            }}
          >
            <VolumeUp fontSize="small" />
          </IconButton>
        </Tooltip>
        
        <Tooltip title={t('control.restart')}>
          <IconButton
            onClick={handleFormat}
            size='medium'
            sx={{
              bgcolor: theme.palette.mode === 'light' ? '#F5F5F5' : '#424242',
              color: theme.palette.mode === 'light' ? '#616161' : '#FFF',
              borderRadius: '8px',
              '&:hover': { bgcolor: theme.palette.mode === 'light' ? '#E0E0E0' : '#3A3A3A' }
            }}
          >
            <RestartAlt fontSize="small" />
          </IconButton>
        </Tooltip>
        
        <Tooltip title={hasForceLocation ? t('control.forceLocationSet') : t('control.setLocation')}>
          <IconButton
            onClick={() => setForceLocationOpen(true)}
            size='medium'
            sx={{
              bgcolor: hasForceLocation
                ? (theme.palette.mode === 'light' ? '#E3F2FD' : '#1565C0')
                : (theme.palette.mode === 'light' ? '#F5F5F5' : '#424242'),
              color: hasForceLocation
                ? (theme.palette.mode === 'light' ? '#1565C0' : '#FFF')
                : (theme.palette.mode === 'light' ? '#616161' : '#FFF'),
              borderRadius: '8px',
              '&:hover': {
                bgcolor: hasForceLocation
                  ? (theme.palette.mode === 'light' ? '#BBDEFB' : '#0D47A1')
                  : (theme.palette.mode === 'light' ? '#E0E0E0' : '#3A3A3A')
              }
            }}
          >
            <PinDrop fontSize='small' />
          </IconButton>
        </Tooltip>

        <Tooltip title={t('control.lastTripPhoto')}>
          <IconButton
            onClick={() => setTripPhotosOpen(true)}
            size='medium'
            sx={{
              bgcolor: theme.palette.mode === 'light' ? '#F5F5F5' : '#424242',
              color: theme.palette.mode === 'light' ? '#616161' : '#FFF',
              borderRadius: '8px',
              '&:hover': { bgcolor: theme.palette.mode === 'light' ? '#E0E0E0' : '#3A3A3A' }
            }}
          >
            <PhotoCamera fontSize='small' />
          </IconButton>
        </Tooltip>

        {onOpenSettings && (
          <Tooltip title={t('control.scooterSettings')}>
            <IconButton
              onClick={onOpenSettings}
              size='medium'
              sx={{
                bgcolor: theme.palette.mode === 'light' ? '#F5F5F5' : '#424242',
                color: theme.palette.mode === 'light' ? '#616161' : '#FFF',
                borderRadius: '8px',
                '&:hover': { bgcolor: theme.palette.mode === 'light' ? '#E0E0E0' : '#3A3A3A' }
              }}
            >
              <Settings fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      <ForceLocationModal
        open={forceLocationOpen}
        onClose={() => setForceLocationOpen(false)}
        scooter={scooter}
      />

      <TripPhotosModal
        open={tripPhotosOpen}
        onClose={() => setTripPhotosOpen(false)}
        scooterId={scooter._id}
        scooterName={scooter.name}
      />
    </Box>
  );
};

export default ScooterInfoWindow;
